import { useEffect, useMemo, useRef, useState } from "react";

/*
  Counts only seconds that were actually played.
  - On every "timeupdate" we compare the new position with the last one.
  - The difference is added only if it is > 0 and < 3 seconds.
    A pause adds nothing (no events), a skip adds nothing (difference too big).
  - On "seeked" we reset the last position so the jump itself is never counted.
*/
export function useWatchTime(player, unlockAfterMinutes) {
  const [watched, setWatched] = useState(0);
  const [duration, setDuration] = useState(null);
  const lastPosition = useRef(null);
  const total = useRef(0);

  useEffect(() => {
    if (!player) return undefined;

    player.getDuration().then(setDuration).catch(() => {});

    const onTimeUpdate = ({ seconds }) => {
      if (lastPosition.current !== null) {
        const diff = seconds - lastPosition.current;
        if (diff > 0 && diff < 3) {
          total.current += diff;
          setWatched(total.current);
        }
      }
      lastPosition.current = seconds;
    };
    const onSeeked = ({ seconds }) => {
      lastPosition.current = seconds;
    };

    player.on("timeupdate", onTimeUpdate);
    player.on("seeked", onSeeked);
    return () => {
      player.off("timeupdate", onTimeUpdate);
      player.off("seeked", onSeeked);
    };
  }, [player]);

  // If the video is shorter than the target, unlock 30 seconds before it ends
  const requiredSeconds = useMemo(() => {
    const target = unlockAfterMinutes * 60;
    if (duration && duration < target) return Math.max(duration - 30, 1);
    return target;
  }, [duration, unlockAfterMinutes]);

  return { watched, requiredSeconds, unlocked: watched >= requiredSeconds };
}
